import { tilePips, type Tile } from '../types.js';
import type { DominoRuleSet } from '../variants/types.js';
import { bestTeamSeats } from './standings.js';
import type {
  HandEndInput,
  HandEndOutcome,
  MatchStanding,
  PlayScoreInput,
  ScoringPolicy,
} from './types.js';

/**
 * Score for a single play: if the sum of open ends is a multiple of the
 * variant's scoreMultiple (5 for All Fives), return that sum; otherwise 0.
 */
export function playScore(ruleSet: DominoRuleSet, openEndsSum: number): number {
  if (openEndsSum === 0) return 0;
  return openEndsSum % ruleSet.scoreMultiple === 0 ? openEndsSum : 0;
}

/** Total pip count of a hand (sum of every tile's two halves). */
export function handPips(hand: readonly Tile[]): number {
  let total = 0;
  for (const tile of hand) {
    total += tilePips(tile);
  }
  return total;
}

/** Round to the nearest multiple of the variant's scoreMultiple. Banker's rounding not needed — domino convention is half-up. */
export function roundToScoreMultiple(value: number, ruleSet: DominoRuleSet): number {
  const m = ruleSet.scoreMultiple;
  return Math.round(value / m) * m;
}

/**
 * End-of-hand bonus: per the variant's rule, the loser's remaining pips (or
 * sum-of-losers in partnership) get rounded to the nearest score multiple
 * and awarded to the winner.
 *
 * For 2-player and FFA: the player who went out (winner) gets the sum of
 * EVERY opponent's remaining pip total, rounded.
 *
 * For partnership: the winning team gets the sum of the losing team's pips,
 * rounded.
 *
 * Block ending (no one went out): the player/team with the lowest pip total
 * wins; their score is the difference (or sum of opponents' pips minus their
 * own — depends on variant). For V1 we use the simpler rule: winner gets
 * the sum of every loser's pip total, rounded.
 */
export interface EndOfHandResult {
  /** Per-seat awarded points. */
  readonly awarded: readonly number[];
  /** The seat (or list of seats for partnership) considered the winner. */
  readonly winnerSeats: readonly number[];
  /** True if the hand ended because the boneyard was empty and nobody could play. */
  readonly blocked: boolean;
}

export interface EndOfHandInput {
  readonly hands: readonly (readonly Tile[])[];
  readonly teamOf: readonly number[];
  readonly teamCount: number;
  readonly wentOutSeat: number | null;
  readonly ruleSet: DominoRuleSet;
}

export function endOfHand(input: EndOfHandInput): EndOfHandResult {
  const { hands, teamOf, teamCount, wentOutSeat, ruleSet } = input;
  const teamPips = new Array<number>(teamCount).fill(0);
  for (let seat = 0; seat < hands.length; seat += 1) {
    const team = teamOf[seat]!;
    teamPips[team]! += handPips(hands[seat]!);
  }

  let winningTeam: number;
  let blocked = false;
  if (wentOutSeat !== null) {
    winningTeam = teamOf[wentOutSeat]!;
  } else {
    blocked = true;
    // Lowest-pip team wins. Tie-break: lowest single hand on the team; if
    // still tied, the team whose lowest-index seat is lower.
    let bestTeam = 0;
    let bestPips = Infinity;
    for (let t = 0; t < teamCount; t += 1) {
      if (teamPips[t]! < bestPips) {
        bestPips = teamPips[t]!;
        bestTeam = t;
      }
    }
    winningTeam = bestTeam;
  }

  let bonus = 0;
  for (let t = 0; t < teamCount; t += 1) {
    if (t !== winningTeam) bonus += teamPips[t]!;
  }
  const awardedPerTeam = new Array<number>(teamCount).fill(0);
  awardedPerTeam[winningTeam] = roundToScoreMultiple(bonus, ruleSet);

  const awarded: number[] = new Array<number>(hands.length).fill(0);
  // In partnership, award is recorded once per team — convention: stored
  // against the lowest-seat-index of the team so callers can aggregate.
  for (let seat = 0; seat < hands.length; seat += 1) {
    const team = teamOf[seat]!;
    const isFirstOfTeam = teamOf.indexOf(team) === seat;
    if (isFirstOfTeam) awarded[seat] = awardedPerTeam[team]!;
  }

  const winnerSeats: number[] = [];
  for (let seat = 0; seat < hands.length; seat += 1) {
    if (teamOf[seat] === winningTeam) winnerSeats.push(seat);
  }
  return { awarded, winnerSeats, blocked };
}

/** True iff the tile is the double-five — the only lead that scores under `leadScoresOnlyOnDoubleFive`. */
function isDoubleFive(tile: Tile): boolean {
  return tile[0] === 5 && tile[1] === 5;
}

/** Points-earned scoring shared by All Fives (Muggins) and Телефон. */
export const FIVES_SCORING: ScoringPolicy = {
  onPlay({ ruleSet, openEndsSum, tile, isLead }: PlayScoreInput): number {
    // Телефон rule: the lead only scores if it's [5|5]. Other variants score
    // the lead normally (heaviest-double opener can land on 10, 15, etc.).
    if (isLead && ruleSet.leadScoresOnlyOnDoubleFive && !isDoubleFive(tile)) return 0;
    return playScore(ruleSet, openEndsSum);
  },
  onHandEnd({ hands, teamMap, wentOutSeat, ruleSet }: HandEndInput): HandEndOutcome {
    const result = endOfHand({
      hands,
      teamOf: teamMap.teamOf,
      teamCount: teamMap.teamCount,
      wentOutSeat,
      ruleSet,
    });
    return { ...result, eggs: false, carriedPenalty: 0, baldFinish: false };
  },
  isMatchOver(scores: readonly number[], ruleSet: DominoRuleSet): boolean {
    return scores.some((s) => s >= ruleSet.pointGoal);
  },
  matchWinnerSeats(standing: MatchStanding): readonly number[] {
    return bestTeamSeats(standing, 1);
  },
  rewardSign: 1,
};
